// Mirrors backend/src/main/java/com/kervolt/oms/services/auth/PasswordPolicy.java —
// keep thresholds and reason wording in sync so frontend hints match the backend 400.

const MIN = 8;
const MAX = 64;
const SEQ = 5; // sequential-run length that trips the rule
const KEYBOARD_ROWS = ['qwertyuiop', 'asdfghjkl', 'zxcvbnm', '1234567890'];

// Trimmed inline blocklist — top common entries from
// backend/src/main/resources/security/common-passwords.txt
const COMMON = new Set<string>([
    'password', '123456', '12345678', '123456789', '12345', '1234567', '1234567890',
    'qwerty', 'qwertyuiop', 'abc123', 'password1', 'password123', '111111', '123123',
    '000000', 'iloveyou', 'admin', 'administrator', 'welcome', 'welcome1', 'login',
    'passw0rd', 'letmein', 'monkey', 'dragon', 'sunshine', 'princess', 'football',
    'baseball', 'master', 'shadow', 'superman', 'batman', 'trustno1', 'whatever',
    'qazwsx', 'zxcvbn', 'asdfgh', 'asdfghjkl', '1q2w3e4r', '1qaz2wsx', 'qwe123',
    'test', 'test123', 'root', 'toor', 'changeme', 'secret', 'default', 'user',
]);

const charClasses = (pw: string): number => {
    let lower = false;
    let upper = false;
    let digit = false;
    let symbol = false;
    for (const c of pw) {
        if (/[a-z]/.test(c)) lower = true;
        else if (/[A-Z]/.test(c)) upper = true;
        else if (/[0-9]/.test(c)) digit = true;
        else symbol = true;
    }
    return (lower ? 1 : 0) + (upper ? 1 : 0) + (digit ? 1 : 0) + (symbol ? 1 : 0);
};

const matchesUsername = (pw: string, username?: string): boolean => {
    if (username === undefined || username === null || username.trim().length < 3) return false;
    const p = pw.toLowerCase();
    const u = username.trim().toLowerCase();

    if (p === u) return true;
    const stripped = p.replace(/\d+$/, '');
    if (stripped === u) return true;

    if (u.length < 4) return false;
    if (p.includes(u)) return true;
    const reversed = u.split('').reverse().join('');
    return p.includes(reversed);
};

const matchesPhone = (pw: string, phone?: string): boolean => {
    if (phone === undefined || phone === null) return false;
    const phoneDigits = phone.replace(/\D/g, '');
    if (phoneDigits.length < 6) return false;
    const pwDigits = pw.replace(/\D/g, '');
    return pwDigits.includes(phoneDigits);
};

const isCommon = (pw: string): boolean => {
    const p = pw.toLowerCase();
    if (COMMON.has(p)) return true;
    const base = p.replace(/\d+$/, '');
    return base !== '' && COMMON.has(base);
};

const isAllSameChar = (pw: string): boolean => {
    return pw.length >= 2 && new Set(pw.split('')).size === 1;
};

const hasSequentialRun = (pw: string): boolean => {
    const p = pw.toLowerCase();
    // straight ascending/descending over char codes (covers abcde and 12345)
    let run = 1;
    for (let i = 1; i < p.length; i++) {
        const d = p.charCodeAt(i) - p.charCodeAt(i - 1);
        run = d === 1 || d === -1 ? run + 1 : 1;
        if (run >= SEQ) return true;
    }
    // keyboard rows (forward and reverse)
    for (const row of KEYBOARD_ROWS) {
        const rev = row.split('').reverse().join('');
        for (let i = 0; i + SEQ <= row.length; i++) {
            if (p.includes(row.substring(i, i + SEQ)) || p.includes(rev.substring(i, i + SEQ))) return true;
        }
    }
    return false;
};

const isRepeatedUnit = (pw: string): boolean => {
    if (pw.length < 4) return false;
    for (let unit = 1; unit <= 3; unit++) {
        if (pw.length % unit !== 0) continue;
        const u = pw.substring(0, unit);
        if (u.repeat(pw.length / unit) === pw) return true;
    }
    return false;
};

export const validatePassword = (password: string, username?: string, phone?: string): string[] => {
    const reasons: string[] = [];
    const pw = password ?? '';

    if (pw.length < MIN) reasons.push(`must be at least ${MIN} characters`);
    if (pw.length > MAX) reasons.push(`must be at most ${MAX} characters`);
    if (charClasses(pw) < 3) reasons.push('must mix at least 3 character types (lowercase, uppercase, digit, symbol)');
    if (matchesUsername(pw, username)) reasons.push('must not be based on your username');
    if (matchesPhone(pw, phone)) reasons.push('must not contain your phone number');
    if (isCommon(pw)) reasons.push('is too common');
    if (isAllSameChar(pw)) reasons.push('must not repeat a single character');
    if (hasSequentialRun(pw)) reasons.push('must not contain a sequence like abcde or 12345');
    if (isRepeatedUnit(pw)) reasons.push('must not repeat a short pattern');

    return reasons;
};
